import { toPng, toSvg } from 'html-to-image';
import type { UnifiedDiagramData, ExtendedExportFormat } from '@veodiagram/shared';

/**
 * Export options for diagram export.
 */
export interface ExportOptions {
  format: ExtendedExportFormat;
  filename?: string;
  backgroundColor?: string;
  padding?: number;
  scale?: number;
}

const defaultExportOptions: Partial<ExportOptions> = {
  backgroundColor: '#ffffff',
  padding: 20,
  scale: 2,
};

/**
 * Export a DOM element as PNG.
 */
export async function exportToPng(
  element: HTMLElement,
  options: Partial<ExportOptions> = {}
): Promise<void> {
  const opts = { ...defaultExportOptions, ...options };

  try {
    const dataUrl = await toPng(element, {
      backgroundColor: opts.backgroundColor,
      pixelRatio: opts.scale,
      style: {
        padding: `${opts.padding}px`,
      },
    });

    // Download the image
    const link = document.createElement('a');
    link.download = opts.filename || 'diagram.png';
    link.href = dataUrl;
    link.click();
  } catch (error) {
    console.error('Failed to export PNG:', error);
    throw new Error('Failed to export diagram as PNG');
  }
}

/**
 * Export a DOM element as SVG.
 */
export async function exportToSvg(
  element: HTMLElement,
  options: Partial<ExportOptions> = {}
): Promise<void> {
  const opts = { ...defaultExportOptions, ...options };

  try {
    const dataUrl = await toSvg(element, {
      backgroundColor: opts.backgroundColor,
      style: {
        padding: `${opts.padding}px`,
      },
    });

    // Download the SVG
    const link = document.createElement('a');
    link.download = opts.filename || 'diagram.svg';
    link.href = dataUrl;
    link.click();
  } catch (error) {
    console.error('Failed to export SVG:', error);
    throw new Error('Failed to export diagram as SVG');
  }
}

/**
 * Export diagram data as JSON.
 */
export function exportToJson(
  data: UnifiedDiagramData,
  options: Partial<ExportOptions> = {}
): void {
  const opts = { ...defaultExportOptions, ...options };

  try {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.download = opts.filename || 'diagram.json';
    link.href = url;
    link.click();

    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export JSON:', error);
    throw new Error('Failed to export diagram as JSON');
  }
}

/**
 * Copy text to clipboard.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    // Fallback for older browsers
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    } catch (fallbackError) {
      console.error('Failed to copy to clipboard:', fallbackError);
      return false;
    }
  }
}

/**
 * Get file extension for export format.
 */
export function getExportFileExtension(format: ExtendedExportFormat): string {
  switch (format) {
    case 'png':
      return 'png';
    case 'svg':
      return 'svg';
    case 'json':
      return 'json';
    default:
      return 'txt';
  }
}

/**
 * Generate filename for export.
 */
export function generateExportFilename(
  title: string,
  format: ExtendedExportFormat
): string {
  const sanitized = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const timestamp = new Date().toISOString().slice(0, 10);
  const extension = getExportFileExtension(format);
  return `${sanitized}-${timestamp}.${extension}`;
}

/**
 * Export diagram based on format.
 */
export async function exportDiagram(
  data: UnifiedDiagramData,
  element: HTMLElement | null,
  format: ExtendedExportFormat,
  options: Partial<ExportOptions> = {}
): Promise<void> {
  const filename = generateExportFilename(data.metadata.title, format);
  const opts = { ...options, filename };

  switch (format) {
    case 'png':
      if (!element) throw new Error('Element required for PNG export');
      await exportToPng(element, opts);
      break;
    case 'svg':
      if (!element) throw new Error('Element required for SVG export');
      await exportToSvg(element, opts);
      break;
    case 'json':
      exportToJson(data, opts);
      break;
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
}
